import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, CheckCircle2, FileText, Mail, Image, 
  ClipboardList, Calculator, Send, ChevronDown, ChevronRight,
  AlertCircle, DollarSign, Clock, Shield, Zap, ArrowDown,
  Trophy, Truck, Banknote, Users, FileCheck, Receipt, CreditCard, Share2
} from 'lucide-react';
import RequestSetupModal from '../components/ui/request-setup-modal';
import ShareModal from '../components/ui/share-modal';

// Before/After Mockup Component
const BeforeAfterMockup = () => {
  return (
    <div className="w-full max-w-5xl mx-auto">
      {/* Desktop Layout */}
      <div className="hidden md:flex items-center justify-between gap-4">
        {/* Before - Builder Documents */}
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="flex-1 bg-slate-100 rounded-2xl p-6 border-2 border-slate-200"
        >
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Builder sends...</div>
          <div className="space-y-3">
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
              <Mail className="w-8 h-8 text-blue-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Email Thread</p>
                <p className="text-xs text-slate-500">RE: RE: FW: Pricing request...</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
              <FileText className="w-8 h-8 text-red-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Tender_Scope_v3.pdf</p>
                <p className="text-xs text-slate-500">42 pages</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
              <Image className="w-8 h-8 text-green-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Site_Photos.zip</p>
                <p className="text-xs text-slate-500">23 images</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
              <FileText className="w-8 h-8 text-purple-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Plans_Rev_C.dwg</p>
                <p className="text-xs text-slate-500">Floor plans</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Arrow */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.5 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="flex flex-col items-center gap-2"
        >
          <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center shadow-lg">
            <Zap className="w-8 h-8 text-white" />
          </div>
          <p className="text-xs font-bold text-orange-500 text-center">2TAP<br/>EXTRACTS</p>
        </motion.div>

        {/* After - 2Tap Output */}
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="flex-1 bg-gradient-to-br from-emerald-50 to-white rounded-2xl p-6 border-2 border-emerald-200"
        >
          <div className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-4">2Tap creates...</div>
          <div className="space-y-3">
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm border border-emerald-100">
              <ClipboardList className="w-8 h-8 text-emerald-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Scope of Works Draft</p>
                <p className="text-xs text-emerald-600">Ready to review</p>
              </div>
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm border border-emerald-100">
              <Calculator className="w-8 h-8 text-emerald-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Costing Checklist</p>
                <p className="text-xs text-emerald-600">Labour, materials, margins</p>
              </div>
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm border border-emerald-100">
              <Send className="w-8 h-8 text-emerald-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Quote Draft</p>
                <p className="text-xs text-emerald-600">Ready to customize & send</p>
              </div>
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm border border-emerald-100">
              <AlertCircle className="w-8 h-8 text-amber-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Questions & Exclusions</p>
                <p className="text-xs text-amber-600">Items to clarify</p>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden space-y-4">
        {/* Before */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-slate-100 rounded-xl p-4 border-2 border-slate-200"
        >
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Builder sends...</div>
          <div className="flex flex-wrap gap-2">
            <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 shadow-sm">
              <Mail className="w-5 h-5 text-blue-500" />
              <span className="text-xs font-medium">Emails</span>
            </div>
            <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 shadow-sm">
              <FileText className="w-5 h-5 text-red-500" />
              <span className="text-xs font-medium">PDFs</span>
            </div>
            <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 shadow-sm">
              <Image className="w-5 h-5 text-green-500" />
              <span className="text-xs font-medium">Photos</span>
            </div>
            <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 shadow-sm">
              <FileText className="w-5 h-5 text-purple-500" />
              <span className="text-xs font-medium">Plans</span>
            </div>
          </div>
        </motion.div>

        {/* Arrow */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.5 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="flex justify-center"
        >
          <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center shadow-lg">
            <ArrowDown className="w-6 h-6 text-white" />
          </div>
        </motion.div>

        {/* After */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-br from-emerald-50 to-white rounded-xl p-4 border-2 border-emerald-200"
        >
          <div className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-3">2Tap creates...</div>
          <div className="space-y-2">
            <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 shadow-sm border border-emerald-100">
              <ClipboardList className="w-5 h-5 text-emerald-500" />
              <span className="text-xs font-medium flex-1">Scope Draft</span>
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            </div>
            <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 shadow-sm border border-emerald-100">
              <Calculator className="w-5 h-5 text-emerald-500" />
              <span className="text-xs font-medium flex-1">Costing Checklist</span>
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            </div>
            <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 shadow-sm border border-emerald-100">
              <Send className="w-5 h-5 text-emerald-500" />
              <span className="text-xs font-medium flex-1">Quote Draft</span>
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// Example Before/After Detail Component with Tabs
const ExampleBeforeAfter = () => {
  const [activeTab, setActiveTab] = useState('scope');

  const tabs = [
    { id: 'scope', label: 'Scope', icon: ClipboardList },
    { id: 'estimate', label: 'Estimate Checklist', icon: Calculator },
    { id: 'quote', label: 'Quote + Email', icon: Send }
  ];

  return (
    <div className="max-w-5xl mx-auto">
      {/* Section Header */}
      <div className="text-center mb-8">
        <h3 className="text-lg md:text-xl font-bold text-slate-900 mb-2">
          After: 2Tap turns the builder documents into quote-ready outputs
        </h3>
        <p className="text-sm text-slate-600">
          From one builder request, 2Tap creates the working pieces needed before the quote is sent.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Before - Builder Request */}
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200"
        >
          <div className="bg-slate-800 px-4 py-3">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Before: Builder Email</p>
          </div>
          <div className="p-4 space-y-3">
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-500 mb-1">From: david@smithbuilders.com.au</p>
              <p className="text-xs text-slate-500 mb-2">Subject: RE: FW: Pricing - External Rendering Works</p>
              <p className="text-sm text-slate-700 leading-relaxed">
                Hey mate,<br/><br/>
                Can you price the external rendering for the Harris project? Attached are the plans and scope from the architect. Need it by Friday if possible.<br/><br/>
                Cheers,<br/>Dave
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <div className="flex items-center gap-1.5 bg-slate-100 rounded px-2 py-1">
                <FileText className="w-3.5 h-3.5 text-red-500" />
                <span className="text-xs text-slate-600">Scope_v2.pdf</span>
              </div>
              <div className="flex items-center gap-1.5 bg-slate-100 rounded px-2 py-1">
                <FileText className="w-3.5 h-3.5 text-blue-500" />
                <span className="text-xs text-slate-600">Plans.pdf</span>
              </div>
              <div className="flex items-center gap-1.5 bg-slate-100 rounded px-2 py-1">
                <Image className="w-3.5 h-3.5 text-green-500" />
                <span className="text-xs text-slate-600">3 photos</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* After - 2Tap Output with Tabs */}
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-lg overflow-hidden border border-emerald-200"
        >
          <div className="bg-emerald-600 px-4 py-3">
            <p className="text-xs font-bold text-emerald-100 uppercase tracking-wider">After: 2Tap Output</p>
          </div>
          
          {/* Desktop Tabs */}
          <div className="hidden md:flex border-b border-slate-200">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-semibold transition-all ${
                  activeTab === tab.id 
                    ? 'bg-emerald-50 text-emerald-700 border-b-2 border-emerald-500' 
                    : 'text-slate-500 hover:bg-slate-50'
                }`}
              >
                <tab.icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Mobile Tab Selector */}
          <div className="md:hidden flex border-b border-slate-200 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-shrink-0 flex items-center gap-1 px-3 py-2 text-[10px] font-semibold transition-all ${
                  activeTab === tab.id 
                    ? 'bg-emerald-50 text-emerald-700 border-b-2 border-emerald-500' 
                    : 'text-slate-500'
                }`}
              >
                <tab.icon className="w-3 h-3" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="p-4 max-h-[400px] overflow-y-auto">
            {/* Scope Tab */}
            {activeTab === 'scope' && (
              <div className="space-y-3">
                <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100">
                  <p className="text-xs font-bold text-emerald-700 mb-2 flex items-center gap-1.5">
                    <ClipboardList className="w-3.5 h-3.5" />
                    SCOPE SUMMARY
                  </p>
                </div>

                {/* Job Details */}
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Job Details</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                    <div><span className="text-slate-500">Type:</span> <span className="font-medium">External Rendering</span></div>
                    <div><span className="text-slate-500">Project:</span> <span className="font-medium">Harris Residence</span></div>
                    <div><span className="text-slate-500">Builder:</span> <span className="font-medium">Smith Builders</span></div>
                    <div><span className="text-slate-500">Due:</span> <span className="font-medium text-amber-600">Friday</span></div>
                    <div><span className="text-slate-500">Area:</span> <span className="font-medium">245m² external render</span></div>
                    <div><span className="text-slate-500">Access:</span> <span className="font-medium">Scaffold required</span></div>
                  </div>
                </div>

                {/* What Needs Pricing */}
                <div className="bg-white rounded-lg p-3 border border-slate-200">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">What Needs Pricing</p>
                  <div className="space-y-1">
                    {['External render — 245m²', 'Scaffold access', 'Surface preparation', 'Corner beading', 'Final coat texture'].map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Questions to Clarify */}
                <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                  <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider mb-2">Questions to Clarify</p>
                  <div className="space-y-1">
                    {['Texture finish type?', 'Colour selection?', 'Access restrictions?', 'Who supplies scaffold?', 'Is substrate repair included or excluded?'].map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-amber-700">
                        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Estimate Checklist Tab */}
            {activeTab === 'estimate' && (
              <div className="space-y-3">
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                  <p className="text-xs font-bold text-blue-700 mb-2 flex items-center gap-1.5">
                    <Calculator className="w-3.5 h-3.5" />
                    ESTIMATE / COSTING TEMPLATE
                  </p>
                </div>

                {/* Costing Table */}
                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold text-slate-600">Item</th>
                        <th className="text-left px-3 py-2 font-semibold text-slate-600">Details</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      <tr><td className="px-3 py-2 font-medium">Labour</td><td className="px-3 py-2 text-slate-600">Crew days / hourly allowance</td></tr>
                      <tr><td className="px-3 py-2 font-medium">Materials</td><td className="px-3 py-2 text-slate-600">Render, texture coat, beads, primer</td></tr>
                      <tr><td className="px-3 py-2 font-medium">Equipment / Access</td><td className="px-3 py-2 text-slate-600">Scaffold, ladders, protection</td></tr>
                      <tr><td className="px-3 py-2 font-medium">Delivery / Travel</td><td className="px-3 py-2 text-slate-600">Site travel, delivery fees</td></tr>
                      <tr><td className="px-3 py-2 font-medium">Waste / Clean-up</td><td className="px-3 py-2 text-slate-600">Bags, disposal, clean-up time</td></tr>
                      <tr><td className="px-3 py-2 font-medium">Risk Allowance</td><td className="px-3 py-2 text-slate-600">Weather, access, substrate condition</td></tr>
                      <tr className="bg-slate-50"><td className="px-3 py-2 font-semibold">Margin</td><td className="px-3 py-2 text-slate-600">Add business margin</td></tr>
                      <tr className="bg-slate-50"><td className="px-3 py-2 font-semibold">GST</td><td className="px-3 py-2 text-slate-600">Check GST treatment</td></tr>
                    </tbody>
                  </table>
                </div>

                {/* Estimate Notes */}
                <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                  <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider mb-2">Estimate Notes</p>
                  <div className="space-y-1 text-xs text-amber-800">
                    <p>• Confirm final measurements before pricing</p>
                    <p>• Clarify scaffold responsibility</p>
                    <p>• Confirm finish and colour before quote is issued</p>
                    <p>• Allow for substrate preparation only if included</p>
                  </div>
                </div>
              </div>
            )}

            {/* Quote + Email Tab */}
            {activeTab === 'quote' && (
              <div className="space-y-3">
                <div className="bg-orange-50 rounded-lg p-3 border border-orange-100">
                  <p className="text-xs font-bold text-orange-700 mb-2 flex items-center gap-1.5">
                    <Send className="w-3.5 h-3.5" />
                    QUOTE OUTLINE + EMAIL DRAFT
                  </p>
                </div>

                {/* Quote Outline */}
                <div className="bg-white rounded-lg p-3 border border-slate-200">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Quote Outline</p>
                  <div className="space-y-2 text-xs">
                    <div><span className="text-slate-500">Project:</span> <span className="font-medium">Harris Residence</span></div>
                    <div><span className="text-slate-500">Builder:</span> <span className="font-medium">Smith Builders</span></div>
                    <div><span className="text-slate-500">Scope:</span> <span className="font-medium">External rendering works to nominated areas</span></div>
                    
                    <div className="pt-2">
                      <p className="text-[10px] font-bold text-emerald-600 mb-1">Inclusions:</p>
                      <div className="pl-2 space-y-0.5 text-slate-600">
                        <p>• Labour</p>
                        <p>• Surface preparation</p>
                        <p>• Render application</p>
                        <p>• Corner beads</p>
                        <p>• Final coat texture</p>
                      </div>
                    </div>

                    <div className="pt-2">
                      <p className="text-[10px] font-bold text-amber-600 mb-1">Exclusions / Assumptions:</p>
                      <div className="pl-2 space-y-0.5 text-slate-600">
                        <p>• Scaffold supplied by others unless confirmed</p>
                        <p>• Substrate repairs excluded unless agreed</p>
                        <p>• Colour and finish to be confirmed</p>
                        <p>• Works subject to site access and weather</p>
                      </div>
                    </div>

                    <div className="pt-2 flex gap-4 text-slate-600">
                      <div><span className="font-medium">Payment:</span> To be confirmed</div>
                      <div><span className="font-medium">Validity:</span> 14 days</div>
                    </div>
                  </div>
                </div>

                {/* Email Draft */}
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Email Draft</p>
                  <div className="bg-white rounded border border-slate-200 p-3 text-xs">
                    <p className="text-slate-500 text-[10px] mb-2">Subject: Quote for External Rendering Works — Harris Residence</p>
                    <div className="text-slate-700 space-y-2 leading-relaxed">
                      <p>Hi [Name],</p>
                      <p>Thanks for sending through the documents for the Harris Residence project.</p>
                      <p>We have reviewed the supplied details and prepared a quote outline for the external rendering works.</p>
                      <p>Before finalising, can you please confirm:</p>
                      <ul className="list-disc pl-4 space-y-0.5">
                        <li>texture finish type</li>
                        <li>colour selection</li>
                        <li>scaffold responsibility</li>
                        <li>any access restrictions</li>
                        <li>whether substrate repairs are included or excluded</li>
                      </ul>
                      <p>Once confirmed, we can finalise the pricing and send through the completed quote.</p>
                      <p>Regards,<br/>[Business Name]</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Next Action */}
          <div className="p-4 pt-0">
            <div className="bg-orange-500 rounded-lg p-3 text-center cursor-pointer hover:bg-orange-600 transition-colors">
              <p className="text-xs font-bold text-white flex items-center justify-center gap-2">
                Next: Add pricing and finalise quote
                <ArrowRight className="w-4 h-4" />
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Mobile Stacked Cards Alternative */}
      <div className="md:hidden mt-8 space-y-4">
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider text-center mb-4">All 2Tap Outputs:</p>
        
        {/* Scope Card */}
        <div className="bg-white rounded-xl shadow-sm border border-emerald-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
              <ClipboardList className="w-4 h-4 text-white" />
            </div>
            <p className="text-sm font-bold text-slate-900">Scope Summary</p>
          </div>
          <p className="text-xs text-slate-600">Job details, what needs pricing, and questions to clarify — all extracted from the builder documents.</p>
        </div>

        {/* Estimate Card */}
        <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
              <Calculator className="w-4 h-4 text-white" />
            </div>
            <p className="text-sm font-bold text-slate-900">Estimate Checklist</p>
          </div>
          <p className="text-xs text-slate-600">Labour, materials, equipment, delivery, waste, risk allowance, margin, and GST — nothing missed.</p>
        </div>

        {/* Quote + Email Card */}
        <div className="bg-white rounded-xl shadow-sm border border-orange-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
              <Send className="w-4 h-4 text-white" />
            </div>
            <p className="text-sm font-bold text-slate-900">Quote + Email Draft</p>
          </div>
          <p className="text-xs text-slate-600">Quote outline with inclusions, exclusions, and a draft email ready to send to the builder.</p>
        </div>
      </div>
    </div>
  );
};

// Workflow Step Component
const WorkflowStep = ({ icon: Icon, label, isActive, isCompleted }) => (
  <div className={`flex flex-col items-center gap-2 ${isActive ? 'scale-110' : ''}`}>
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all ${
      isCompleted ? 'bg-emerald-500 text-white' : 
      isActive ? 'bg-orange-500 text-white shadow-lg' : 
      'bg-slate-200 text-slate-500'
    }`}>
      <Icon className="w-6 h-6" />
    </div>
    <p className={`text-xs font-medium text-center max-w-[80px] ${isActive ? 'text-orange-600' : 'text-slate-600'}`}>
      {label}
    </p>
  </div>
);

// Main Page Component
const FeatureScopeQuote = () => {
  const navigate = useNavigate();
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Navigation links for related areas
  const relatedAreas = [
    { icon: Users, label: 'Leads & Referrals', href: '/features/leads-referrals', color: 'emerald' },
    { icon: ClipboardList, label: 'Scope & Quotes', href: '/features/scope-costing-quote', color: 'emerald', active: true },
    { icon: FileText, label: 'SWMS & Safety', href: '/features/swms-safety', color: 'blue' },
    { icon: Truck, label: 'Site Updates', href: '/features/site-updates', color: 'blue' },
    { icon: Receipt, label: 'Invoicing', href: '/features', color: 'orange' },
    { icon: CreditCard, label: 'Payments', href: '/features', color: 'orange' }
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {/* Navigation - Consistent with Landing Page */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-xl border-b border-slate-200 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 flex items-center justify-between">
          <div 
            className="flex items-center gap-2 cursor-pointer" 
            onClick={() => navigate('/')}
          >
            <div className="w-10 h-10 bg-[#1F2A37] rounded-lg flex items-center justify-center">
              <span className="text-orange-500 font-bold text-xl" style={{ fontFamily: 'Chivo, sans-serif' }}>2</span>
            </div>
            <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>2TAP</span>
          </div>
          
          <div className="hidden md:flex items-center gap-8">
            <button 
              onClick={() => navigate('/')}
              className="text-slate-600 hover:text-slate-900 text-sm font-medium tracking-wide"
            >
              HOME
            </button>
            <button 
              onClick={() => navigate('/features')}
              className="text-orange-500 text-sm font-medium tracking-wide"
            >
              FEATURES
            </button>
            <button 
              onClick={() => setShowShareModal(true)} 
              className="text-slate-600 hover:text-slate-900 text-sm font-medium tracking-wide flex items-center gap-1.5"
            >
              <Share2 className="w-4 h-4" />
              SHARE
            </button>
            <Button 
              onClick={() => navigate('/login')} 
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm px-6 font-semibold tracking-wide shadow-[3px_3px_0px_rgba(0,0,0,0.2)]"
            >
              LOG IN
            </Button>
          </div>

          <div className="md:hidden flex items-center gap-1">
            <button 
              onClick={() => navigate('/features')}
              className="text-orange-500 text-xs font-medium px-2"
            >
              FEATURES
            </button>
            <button 
              onClick={() => setShowShareModal(true)} 
              className="p-1.5 text-slate-600 hover:text-slate-900"
            >
              <Share2 className="w-5 h-5" />
            </button>
            <Button 
              onClick={() => navigate('/login')} 
              size="sm" 
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm text-xs px-3"
            >
              LOG IN
            </Button>
          </div>
        </div>
      </nav>

      {/* 1. Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 px-4 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-64 h-64 bg-orange-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-emerald-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-6xl mx-auto">
          {/* Feature Label */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/30 text-orange-400 px-3 py-1.5 rounded-full text-xs font-semibold mb-6 tracking-wider"
          >
            <Calculator className="w-4 h-4" />
            SCOPE, COSTING & QUOTE
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Left - Text */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              {/* Hook */}
              <p className="text-orange-400 text-sm md:text-base font-semibold mb-3">
                Builder sent documents to price?
              </p>
              
              {/* Headline */}
              <h1 
                className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight mb-6"
                style={{ fontFamily: 'Chivo, sans-serif' }}
              >
                PRICE JOBS FASTER,<br />
                <span className="text-orange-500">FROM SCOPE TO QUOTE</span>
              </h1>

              {/* Subheading */}
              <p className="text-slate-300 text-sm md:text-base mb-4 leading-relaxed">
                Upload or forward the builder email, plans, scope, PO, tender invite or site notes. 
                2Tap helps extract what matters and turns it into a Scope of Works draft, costing checklist, and quote-ready draft.
              </p>

              {/* Supporting Line */}
              <p className="text-slate-400 text-sm border-l-2 border-orange-500 pl-3 mb-8">
                You stay in control of the final price. 2Tap helps you respond faster. Less stress, more quoting.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={() => setShowSetupModal(true)}
                  className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm px-6 py-3 font-bold"
                >
                  REGISTER INTEREST
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>

            {/* Right - Visual Mockup */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="hidden md:block"
            >
              <BeforeAfterMockup />
            </motion.div>
          </div>

          {/* Mobile Mockup */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="md:hidden mt-10"
          >
            <BeforeAfterMockup />
          </motion.div>
        </div>
      </section>

      {/* 2. Problem Section */}
      <section className="py-16 md:py-24 px-4 bg-slate-100">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 text-red-500 text-xs font-bold tracking-widest mb-4">
              <AlertCircle className="w-4 h-4" />
              THE PROBLEM
            </div>
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900 mb-6"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              PRICING TAKES LONGER WHEN<br />
              <span className="text-red-500">JOB DETAILS ARE SCATTERED</span>
            </h2>
            <p className="text-slate-600 text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
              The job is worth pricing, but the details are buried in messy email threads and attachments. 
              It's hard to work out what to include, what to exclude, and what questions to ask before you can quote.
            </p>
          </motion.div>

          {/* Pain Points */}
          <div className="grid sm:grid-cols-3 gap-4 mt-10">
            {[
              { icon: Clock, text: 'Hours spent reading documents' },
              { icon: AlertCircle, text: 'Missing details cause requotes' },
              { icon: DollarSign, text: 'Margins slip through the cracks' }
            ].map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-xl p-4 shadow-sm"
              >
                <item.icon className="w-8 h-8 text-red-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-700">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. What 2Tap Creates Section */}
      <section className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 text-emerald-500 text-xs font-bold tracking-widest mb-4">
              <CheckCircle2 className="w-4 h-4" />
              WHAT 2TAP CREATES
            </div>
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              FROM DOCUMENTS TO<br />
              <span className="text-emerald-500">QUOTE-READY OUTPUTS</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { 
                icon: ClipboardList, 
                title: 'Scope of Works Draft', 
                desc: 'Extracted and organized from builder documents',
                color: 'emerald'
              },
              { 
                icon: Calculator, 
                title: 'Costing Checklist', 
                desc: 'Labour, materials, scaffold, GST — nothing missed',
                color: 'blue'
              },
              { 
                icon: Send, 
                title: 'Quote Draft', 
                desc: 'Ready to customize, price, and send',
                color: 'orange'
              }
            ].map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className={`bg-gradient-to-br from-${item.color}-50 to-white rounded-2xl p-6 border border-${item.color}-100 shadow-sm`}
              >
                <div className={`w-14 h-14 bg-${item.color}-500 rounded-xl flex items-center justify-center mb-4`}>
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. How It Works Section */}
      <section className="py-16 md:py-24 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 text-orange-500 text-xs font-bold tracking-widest mb-4">
              <Zap className="w-4 h-4" />
              HOW IT WORKS
            </div>
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              THREE STEPS TO<br />
              <span className="text-orange-500">A FASTER QUOTE</span>
            </h2>
          </motion.div>

          <div className="space-y-6">
            {[
              { 
                step: '1', 
                title: 'Forward or Upload', 
                desc: 'Send the builder email to 2Tap or upload the documents directly.' 
              },
              { 
                step: '2', 
                title: '2Tap Extracts & Organizes', 
                desc: 'AI pulls out job details, scope items, and flags questions.' 
              },
              { 
                step: '3', 
                title: 'Review, Price & Send', 
                desc: 'Add your pricing, review the quote draft, and send it off.' 
              }
            ].map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-sm"
              >
                <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-black text-xl">{item.step}</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-1">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Example Before/After Section */}
      <section className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 text-slate-500 text-xs font-bold tracking-widest mb-4">
              <FileText className="w-4 h-4" />
              EXAMPLE
            </div>
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              SEE THE<br />
              <span className="text-orange-500">TRANSFORMATION</span>
            </h2>
          </motion.div>

          <ExampleBeforeAfter />
        </div>
      </section>

      {/* 6. Why It Matters Section */}
      <section className="py-16 md:py-24 px-4 bg-gradient-to-br from-emerald-600 to-emerald-700">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-6"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              WHY IT MATTERS
            </h2>
            <p className="text-emerald-100 text-lg md:text-xl mb-10 max-w-2xl mx-auto">
              Every hour spent decoding builder documents is an hour not spent on the tools — or winning more work.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { icon: Clock, title: 'Quote Faster', desc: 'Respond while the job is still hot' },
              { icon: Shield, title: 'Protect Margins', desc: 'Never miss a line item again' },
              { icon: DollarSign, title: 'Win More Work', desc: 'More quotes = more wins' }
            ].map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-5"
              >
                <item.icon className="w-10 h-10 text-white mx-auto mb-3" />
                <h3 className="text-lg font-bold text-white mb-1">{item.title}</h3>
                <p className="text-sm text-emerald-100">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. You're In Control Section */}
      <section className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-slate-900 rounded-3xl p-8 md:p-12 text-center"
          >
            <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-4"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              YOU STAY IN CONTROL<br />
              <span className="text-orange-500">OF THE FINAL PRICE</span>
            </h2>
            <p className="text-slate-300 text-sm md:text-base max-w-2xl mx-auto">
              2Tap organizes the information and creates drafts — but you always review the details, 
              check the margins, adjust the pricing, and decide what to send. Nothing goes out without your approval.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 8. Where It Fits Section */}
      <section className="py-16 md:py-24 px-4 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-full text-xs font-bold mb-4">
              <Trophy className="w-4 h-4" />
              PART OF WIN THE WORK
            </div>
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              WHERE IT FITS IN<br />
              <span className="text-emerald-500">2TAP</span>
            </h2>
          </motion.div>

          {/* Workflow */}
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm mb-10">
            <div className="flex flex-wrap justify-center items-center gap-2 md:gap-4">
              <WorkflowStep icon={Mail} label="Builder Docs" isCompleted />
              <ChevronRight className="w-5 h-5 text-slate-300 hidden sm:block" />
              <WorkflowStep icon={ClipboardList} label="Scope Builder" isActive />
              <ChevronRight className="w-5 h-5 text-slate-300 hidden sm:block" />
              <WorkflowStep icon={Calculator} label="Costing" isActive />
              <ChevronRight className="w-5 h-5 text-slate-300 hidden sm:block" />
              <WorkflowStep icon={Send} label="Quote Draft" isActive />
              <ChevronRight className="w-5 h-5 text-slate-300 hidden sm:block" />
              <WorkflowStep icon={FileCheck} label="Quote Sent" />
              <ChevronRight className="w-5 h-5 text-slate-300 hidden sm:block" />
              <WorkflowStep icon={CheckCircle2} label="Accepted" />
            </div>
          </div>

          {/* Related Areas */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {relatedAreas.map((item, i) => (
              <div 
                key={i} 
                onClick={() => navigate(item.href)}
                className={`flex items-center gap-2 rounded-lg p-3 cursor-pointer transition-all hover:scale-[1.02] ${
                  item.active 
                    ? 'bg-emerald-500 text-white' 
                    : 'bg-white border border-slate-200 text-slate-700 hover:border-slate-300 hover:shadow-sm'
                }`}
              >
                <item.icon className={`w-5 h-5 ${item.active ? 'text-white' : `text-${item.color}-500`}`} />
                <span className="text-sm font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. Final CTA Section */}
      <section className="py-16 md:py-24 px-4 bg-slate-900">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-4"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              READY TO QUOTE<br />
              <span className="text-orange-500">SMARTER & FASTER?</span>
            </h2>
            <p className="text-slate-400 text-sm md:text-base mb-8 max-w-xl mx-auto">
              Tradies using 2Tap are winning more work with professional quotes — while others are still chasing paperwork. Set up your business now.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                onClick={() => setShowSetupModal(true)}
                className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm px-8 py-4 font-bold text-lg"
              >
                REGISTER INTEREST
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 px-4 bg-slate-950">
        <div className="max-w-5xl mx-auto flex flex-col items-center gap-4">
          <button 
            onClick={() => setShowSetupModal(true)}
            className="text-slate-400 hover:text-orange-400 text-sm transition-colors"
          >
            Want a custom 2Tap for your business or industry? <span className="underline">Let's talk →</span>
          </button>
          <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">
              © 2025 2Tap. Powered for Smart Business.
            </p>
            <div className="flex items-center gap-6">
              <button onClick={() => { window.scrollTo(0, 0); navigate('/'); }} className="text-slate-400 hover:text-white text-sm transition-colors">Home</button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/features'); }} className="text-slate-400 hover:text-white text-sm transition-colors">Features</button>
              <button onClick={() => setShowShareModal(true)} className="text-slate-400 hover:text-white text-sm transition-colors">Share</button>
            </div>
          </div>
        </div>
      </footer>

      {/* Setup Modal */}
      <RequestSetupModal 
        isOpen={showSetupModal} 
        onClose={() => setShowSetupModal(false)} 
      />

      {/* Share Modal */}
      <ShareModal 
        isOpen={showShareModal} 
        onClose={() => setShowShareModal(false)} 
      />
    </div>
  );
};

export default FeatureScopeQuote;
